import { ICON_COLOR } from '@zep-us/zus/tokens';
import Link from 'next/link';
import { useDistanceToNow } from '@/hooks';
import { ChevronRightIcon, ClockIcon } from '@/icons';
import { DetailHeroCard } from '@/shared/components';
import { type Folder, FolderCategoryChips, FolderThumbnail } from '@/shared/features/quiz-folder';
import { UserAvatar } from '@/shared/features/user-avatar';
import { getUserProfileHref, useOpensUserProfileInNewTab } from '@/shared/hooks/useOpenUserProfile';
import { cn } from '@/utils/tailwind.util';
interface FolderHeroCardProps {
  folder: Folder;
}

/**
 * 폴더 상세 히어로. 셸은 `DetailHeroCard` 가 소유하고 여기서는 슬롯만 채운다.
 *
 * 이미지는 데스크톱과 모바일의 크기·윤곽이 달라 따로 넘긴다.
 */
export const FolderHeroCard = ({
  folder
}: FolderHeroCardProps) => {
  return <DetailHeroCard image={
  // `w-[180px] shrink-0` 을 함께 준다 — 없으면 옆의 `flex-1` 본문에 밀려 폴더가 눌린다.
  // `pb-3xs` 는 썸네일이 아니라 감싸는 쪽에 준다(box-border 라 내용이 줄어든다).
  <div className={'flex w-[180px] shrink-0 flex-col items-start pb-3xs'}>
          <FolderThumbnail imageUrls={folder.thumbnailUrls} color={folder.color} size={'hero'} />
        </div>} mobileImage={<FolderThumbnail imageUrls={folder.thumbnailUrls} color={folder.color} size={'heroMobile'} />} data-sentry-element="DetailHeroCard" data-sentry-component="FolderHeroCard" data-sentry-source-file="FolderHeroCard.tsx">
      <FolderHeroContent folder={folder} data-sentry-element="FolderHeroContent" data-sentry-source-file="FolderHeroCard.tsx" />
    </DetailHeroCard>;
};

/**
 * 히어로 본문. 셸이 모바일·데스크톱에 같은 노드를 넣으므로 타이포 반응형은 여기가 소유한다.
 * 작성자 줄은 데스크톱에만 있다.
 */
const FolderHeroContent = ({
  folder
}: FolderHeroCardProps) => {
  const distanceToNow = useDistanceToNow();
  const opensProfileInNewTab = useOpensUserProfileInNewTab();

  /**
   * 카테고리가 없는 폴더는 칩 줄이 사라져 제목이 위로 붙는다.
   * 데스크톱은 상단 `pt-lg`, 모바일은 빈 칩 줄로 보정한다.
   */
  const hasChips = folder.chips.length > 0;
  return <div className={'flex min-w-px flex-1 flex-col gap-sm'} data-sentry-component="FolderHeroContent" data-sentry-source-file="FolderHeroCard.tsx">
      <div className={cn('flex flex-col gap-xs tablet-sm:gap-[14px]', !hasChips && 'tablet-sm:pt-lg')}>
        <FolderCategoryChips chips={folder.chips} size={'hero'}
      // 데스크톱은 위 `pt-lg` 로 보정하므로 빈 줄을 그리지 않는다.
      emptyPlaceholderClassName={'tablet-sm:hidden'} data-sentry-element="FolderCategoryChips" data-sentry-source-file="FolderHeroCard.tsx" />
        {/*
          ZUS 는 560px 이하에서 타이포 클래스를 한 단계 줄인다.
          `tablet-sm:` 분기를 더하면 축소가 이중으로 걸린다.
         */}
        <h1 className={'text-heading-xl-extrabold text-default'}>
          {folder.name}
        </h1>
      </div>

      {/* 모바일은 세로 스택, 데스크톱은 한 줄이다. 구분선은 데스크톱 전용. */}
      <div className={'flex min-h-2xl w-full flex-col flex-wrap items-start gap-2xs text-body-md-medium text-neutral tablet-sm:flex-row tablet-sm:items-center tablet-sm:gap-x-sm tablet-sm:gap-y-0'}>
        <div className={'flex w-full flex-wrap items-center gap-sm'}>
          {/* 작성자는 데스크톱에만 있다. 이동이므로 앵커다 — 가운데 클릭·새 탭이 그냥 된다.
              기본 클릭의 새 탭 여부는 useOpensUserProfileInNewTab 이 정한다. */}
          <Link href={getUserProfileHref(folder.ownerId)} target={opensProfileInNewTab ? '_blank' : undefined} prefetch={false} className={'group/author hidden shrink-0 items-center gap-2xs no-underline tablet-sm:flex'} data-sentry-element="Link" data-sentry-source-file="FolderHeroCard.tsx">
            <UserAvatar avatarData={folder.ownerAvatarData} size={'sm'} data-sentry-element="UserAvatar" data-sentry-source-file="FolderHeroCard.tsx" />
            <span className={'text-body-md-semibold group-hover/author:text-assistive'}>
              {folder.ownerName}
            </span>
            <ChevronRightIcon size={'xs'} fill={ICON_COLOR.neutral} data-sentry-element="ChevronRightIcon" data-sentry-source-file="FolderHeroCard.tsx" />
          </Link>
          <Divider data-sentry-element="Divider" data-sentry-source-file="FolderHeroCard.tsx" />

          <span className={'flex shrink-0 items-center gap-2xs'}>
            <ClockIcon size={'xs'} data-sentry-element="ClockIcon" data-sentry-source-file="FolderHeroCard.tsx" />
            {distanceToNow(folder.createdAt)}
          </span>
        </div>
      </div>
    </div>;
};

/** 12px 세로 구분선. 데스크톱 전용. */
const Divider = () => <span className={'hidden h-sm w-0 shrink-0 border-l border-neutral tablet-sm:block'} data-sentry-component="Divider" data-sentry-source-file="FolderHeroCard.tsx" />;